// Nauczyciel w lekcji (patch-30): po próbie „powiedz zdanie” bot mówi, co usłyszał i co poprawić — bez modelu językowego.
// Cała wypowiedź wynika z wyniku próby, więc składamy ją z gotowych nagrań (frazy z src/content/teacher.json + nagrania pojedynczych słów w.<słowo>)
// głosem ElevenLabs, w języku wybranym przez rodzica (angielski albo polski). Ten sam szablon daje podpis na ekranie i dźwięk — nie mogą się rozjechać.
// Czysta logika bez DOM i sieci.
import { config } from '../config.ts';
import { teacherContent } from '../content/index.ts';
import { audioIds } from '../speech/audioIds.ts';
import type { AssessmentResult, BanterLine, TeacherContent, TeacherLang, TeacherPhraseKey, Verdict } from '../types.ts';
import { spokenForm, wordKey } from './pronunciation.ts';
import { tokenize } from './text.ts';

/** Jeden klocek wypowiedzi: nagranie `id`, a gdy go brak — tekst do przeczytania głosem zapasowym. */
export interface Clip {
  id: string;
  text: string;
  lang: TeacherLang;
}

export type MoveKind = 'praise' | 'swap' | 'missed' | 'weak' | 'slowly' | 'unclear' | 'wait' | 'retry';

export interface TeacherMove {
  kind: MoveKind;
  clips: Clip[];
  /** Podpis na ekranie (to samo, co mówią klocki). */
  text: string;
  /** Słowo, które dziecko ma powiedzieć (swap, missed, weak). */
  target?: string;
  /** Słowo, które usłyszało rozpoznawanie zamiast docelowego (tylko swap). */
  said?: string;
  /** To była ostatnia próba — na końcu jest „nic się nie stało, idziemy dalej”. */
  final?: boolean;
}

// --- różnice między zdaniem a tym, co usłyszano ---------------------------------------------------------

/** Fragment, w którym zdanie wzorcowe i usłyszany tekst się rozchodzą: słowa wzorca bez pary (`ref`) i słowa usłyszane zamiast nich (`hyp`). */
export interface WordGap {
  ref: string[];
  hyp: string[];
}

/**
 * Porównuje słowo po słowie zdanie wzorcowe z usłyszanym tekstem (najdłuższy wspólny podciąg po dokładnie równych słowach) i zwraca miejsca,
 * w których się rozchodzą, w kolejności zdania. „Do you want to eat” vs „do you want to add” → [{ref:[eat], hyp:[add]}]; brak słowa → hyp puste;
 * dodatkowe słowo → ref puste.
 */
export function wordGaps(reference: string, heard: string): WordGap[] {
  const R = tokenize(reference);
  const H = tokenize(heard);
  const n = R.length;
  const m = H.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] = R[i] === H[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const gaps: WordGap[] = [];
  let i = 0;
  let j = 0;
  let ref: string[] = [];
  let hyp: string[] = [];
  const flush = () => {
    if (ref.length > 0 || hyp.length > 0) gaps.push({ ref, hyp });
    ref = [];
    hyp = [];
  };
  while (i < n && j < m) {
    if (R[i] === H[j]) {
      flush();
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      ref.push(R[i]!);
      i++;
    } else {
      hyp.push(H[j]!);
      j++;
    }
  }
  while (i < n) ref.push(R[i++]!);
  while (j < m) hyp.push(H[j++]!);
  flush();
  return gaps;
}

// --- składanie uwagi ---------------------------------------------------------------------------------------

export interface TeachInput {
  /** Zdanie wzorcowe, które dziecko miało powiedzieć. */
  reference: string;
  result: AssessmentResult;
  verdict: Verdict;
  /** Numer tej próby (od 1) i ile jest dozwolonych. */
  attempt: number;
  maxAttempts: number;
  lang: TeacherLang;
  /** Czy słowo ma nagranie (w.<słowo>). Bot mówi tylko słowa, które ma nagrane — rozpoznawanie nigdy nie wstawi mu do ust przypadkowego słowa. */
  hasClip: (word: string) => boolean;
  /** Ziarno wyboru pochwały (ta sama próba = ta sama pochwała). */
  seed?: number;
  content?: TeacherContent;
}

const QUOTES: Record<TeacherLang, [string, string]> = { en: ['“', '”'], pl: ['„', '”'] };

/** Fragment składany z klocków: fraza + opcjonalne słowo wzorcowe, z podpisem „Fraza „słowo”.”. */
function builder(lang: TeacherLang, content: TeacherContent) {
  const clips: Clip[] = [];
  const caption: string[] = [];
  const [qo, qc] = QUOTES[lang];
  return {
    /** Fraza samodzielna („Almost!”, „Say the whole sentence.”). */
    phrase(key: TeacherPhraseKey): void {
      const text = content.phrases[key][lang];
      clips.push({ id: audioIds.teacher(key, lang), text, lang });
      caption.push(text);
    },
    /** Fraza wprowadzająca i słowo po angielsku: „I heard” + add → „I heard “add”.” */
    lead(key: TeacherPhraseKey, word: string): void {
      const text = content.phrases[key][lang];
      clips.push({ id: audioIds.teacher(key, lang), text, lang }, { id: audioIds.word(word), text: spokenForm(word), lang: 'en' });
      caption.push(`${text} ${qo}${wordKey(word)}${qc}.`);
    },
    praise(id: string): void {
      const p = content.praise.find((x) => x.id === id);
      if (!p) return;
      clips.push({ id: audioIds.teacher(id, lang), text: p[lang], lang });
      caption.push(p[lang]);
    },
    done(kind: MoveKind, extra: Partial<TeacherMove> = {}): TeacherMove {
      return { kind, clips, text: caption.join(' '), ...extra };
    },
  };
}

/** Czy rozpoznawanie jest dość pewne, by powiedzieć „usłyszałem …”: pewność zwykłego rozpoznawania albo zgodność obu rozpoznań. */
function sureAbout(result: AssessmentResult, said: string): boolean {
  const conf = result.transcriptConfidence;
  const sure = conf !== undefined && conf >= config.teacher.heardMinConfidence && (result.transcriptTies ?? 0) <= config.scoring.maxTiedAlternatives;
  const scriptedAgrees = result.scriptedText ? tokenize(result.scriptedText).includes(said) : false;
  return sure || scriptedAgrees;
}

/**
 * Co bot ma powiedzieć po tej próbie. Zaliczona → krótka pochwała. Niezaliczona → JEDNA konkretna uwaga (dziecko nie zapamięta trzech naraz):
 * zamienione słowo („usłyszałem add, powiedz eat”), brakujące słowo, słabo wymówione słowo, a gdy nic konkretnego — zachęta do powtórzenia.
 * Po ostatniej próbie dochodzi „nic się nie stało, idziemy dalej”.
 */
export function teach(input: TeachInput): TeacherMove {
  const content = input.content ?? teacherContent;
  const { result, verdict, lang } = input;
  const b = builder(lang, content);

  if (verdict.passed) {
    const n = content.praise.length;
    b.praise(content.praise[Math.abs(Math.trunc(input.seed ?? input.attempt)) % Math.max(1, n)]?.id ?? '');
    return b.done('praise');
  }

  const final = input.attempt >= input.maxAttempts;
  const almost = verdict.stars >= 2;
  const ok = (w: string | undefined): w is string => w !== undefined && w !== '' && input.hasClip(w);
  const R = tokenize(input.reference);
  const heardText = result.source !== 'self' ? (result.recognizedText ?? '') : '';
  const gaps = heardText.trim() === '' ? [] : wordGaps(input.reference, heardText);

  const finish = (kind: MoveKind, extra: Partial<TeacherMove> = {}): TeacherMove => {
    if (final) b.phrase('moveon');
    return b.done(kind, { ...extra, ...(final ? { final: true } : {}) });
  };
  const say = (target: string, kind: MoveKind = 'weak'): TeacherMove => {
    if (almost) b.phrase('almost');
    b.lead('say', target);
    return finish(kind, { target });
  };

  if (verdict.reason === 'unclear') {
    b.phrase('unclear');
    return finish('unclear');
  }

  // pierwsze słowo zginęło, a reszta jest dobra: zwykle dziecko zaczęło mówić, zanim mikrofon był gotowy
  const only = gaps.length === 1 ? gaps[0]! : null;
  if (verdict.missingFirst && only && only.hyp.length === 0 && only.ref.length === 1 && only.ref[0] === R[0]) {
    b.phrase('wait');
    return finish('wait');
  }

  // zamiana jednego słowa na inne: „usłyszałem add, powiedz eat”
  for (const g of gaps) {
    if (g.ref.length === 1 && g.hyp.length === 1) {
      const target = g.ref[0]!;
      const said = g.hyp[0]!;
      if (!ok(target)) continue;
      if (ok(said) && sureAbout(result, said)) {
        if (almost) b.phrase('almost');
        b.lead('heard', said);
        b.lead('say', target);
        return finish('swap', { target, said });
      }
      return say(target);
    }
  }
  // brakujące słowo
  for (const g of gaps) {
    if (g.ref.length >= 1 && g.hyp.length === 0 && ok(g.ref[0])) {
      if (almost) b.phrase('almost');
      b.lead('missed', g.ref[0]!);
      b.phrase('all');
      return finish('missed', { target: g.ref[0]! });
    }
  }
  // kilka słów w jednym miejscu (np. „eat” → „it is”): nie zgadujemy, co powiedziało dziecko — pokazujemy pierwsze słowo wzorca
  for (const g of gaps) if (g.ref.length >= 1 && ok(g.ref[0])) return say(g.ref[0]!);

  // brak transkryptu (samoocena, tryb zapasowy) albo transkrypt zgodny ze zdaniem, a ocena głosek niska
  const missing = verdict.missing.find((w) => ok(w));
  if (missing) {
    if (almost) b.phrase('almost');
    b.lead('missed', missing);
    b.phrase('all');
    return finish('missed', { target: missing });
  }
  const weak = verdict.weakWords.find((w) => ok(w));
  if (weak) return say(weak);

  if (verdict.reason === 'low-accuracy') {
    if (almost) b.phrase('almost');
    b.phrase('slowly');
    return finish('slowly');
  }
  if (final) {
    // po ostatniej próbie „posłuchaj jeszcze raz” nie ma sensu — tylko pocieszenie
    b.phrase('moveon');
    return b.done('retry', { final: true });
  }
  b.phrase('retry');
  return b.done('retry');
}

/**
 * Linijka bota (powitanie, żart, pożegnanie) w wybranym języku: po polsku gra jej polska wersja (b.<id>.pl, tekst z pola `pl`), o ile angielska linijka
 * ją ma; inaczej zostaje oryginał.
 */
export function lineIn(line: BanterLine, lang: TeacherLang): Clip {
  if (lang === 'pl' && line.lang === 'en' && line.pl) return { id: audioIds.banterPl(line.id), text: line.pl, lang: 'pl' };
  return { id: audioIds.banter(line.id), text: line.text, lang: line.lang };
}
